import type { Root } from 'react-dom/client';
import i18next from 'i18next';
import type { AnySchema } from 'ajv';
import Ajv from 'ajv';
import type { OverviewMap as OLOverviewMap } from 'ol/control';
import { ScaleLine } from 'ol/control';
import Overlay from 'ol/Overlay';
import type { Extent } from 'ol/extent';
import type { FitOptions } from 'ol/View';
import { KeyboardPan, KeyboardZoom } from 'ol/interaction';
import type { Coordinate } from 'ol/coordinate';
import type { Size } from 'ol/size';
import type { Pixel } from 'ol/pixel';

import type {
  TypeBasemapOptions,
  TypeInteraction,
  TypeValidAppBarCoreProps,
  TypeValidFooterBarTabsCoreProps,
  TypeValidMapProjectionCodes,
  TypeViewSettings,
  TypePointMarker,
  TypeHighlightColors,
  TypeMapViewSettings,
  TypeFeatureInfoEntry,
  TypeMapConfig,
  TypeMapFeaturesInstance,
} from '@/api/types/map-schema-types';
import { MAP_EXTENTS, MAX_EXTENTS_RESTRICTION } from '@/api/types/map-schema-types';
import type {
  MapConfigLayerEntry,
  TypeLayerInitialSettings,
  TypeGeoviewLayerConfig,
  TypeLayerEntryConfig,
  TypeLayerStatus,
} from '@/api/types/layer-schema-types';
import { CONST_LAYER_TYPES } from '@/api/types/layer-schema-types';
import { api } from '@/app';
import type { Draw } from '@/geo/interaction/draw';

import { LayerApi, type GeoViewLayerAddedResult } from '@/geo/layer/layer';
import type { TypeMapState, TypeMapMouseInfo } from '@/geo/map/map-viewer';
import { MapViewer } from '@/geo/map/map-viewer';
import type { TypeMapStateForExportLayout } from '@/core/components/export/utilities';
import { Plugin } from '@/api/plugin/plugin';
import type { PluginsContainer } from '@/api/plugin/plugin-types';
import type { AbstractPlugin } from '@/api/plugin/abstract-plugin';
import { Projection } from '@/geo/utils/projection';
import { GeoUtilities } from '@/geo/utils/utilities';
import { getGeoViewStore } from '@/core/stores/stores-managers';
import { DEFAULT_OL_FITOPTIONS, NORTH_POLE_POSITION, OL_ZOOM_DURATION, OL_ZOOM_PADDING } from '@/core/utils/constant';
import { logger } from '@/core/utils/logger';
import { delay, isValidUUID, whenThisThen } from '@/core/utils/utilities';
import type { TimeDimension } from '@/core/utils/date-mgt';
import { DateMgt } from '@/core/utils/date-mgt';

import type { TypeMapFeaturesConfig } from '@/core/types/global-types';
import type { TypeClickMarker } from '@/core/components';
import type { TypeLegendLayer } from '@/core/components/layers/types';
import type { TypeFeatureStyle } from '@/geo/layer/geometry/geometry-types';
import { AbstractEventProcessor } from '@/api/event-processors/abstract-event-processor';
import { AppEventProcessor } from '@/api/event-processors/event-processor-children/app-event-processor';
import { DataTableEventProcessor } from '@/api/event-processors/event-processor-children/data-table-event-processor';
import { TimeSliderEventProcessor } from '@/api/event-processors/event-processor-children/time-slider-event-processor';
import { UIEventProcessor } from '@/api/event-processors/event-processor-children/ui-event-processor';
import { LegendEventProcessor } from '@/api/event-processors/event-processor-children/legend-event-processor';
import type { IMapState, TypeOrderedLayerInfo, TypeScaleInfo } from '@/core/stores/store-interface-and-intial-values/map-state';
import { getAppCrosshairsActive } from '@/core/stores/store-interface-and-intial-values/app-state';
import type { TypeHoverFeatureInfo } from '@/core/stores/store-interface-and-intial-values/feature-info-state';
import { ConfigBaseClass } from '@/api/config/validation-classes/config-base-class';

import { InvalidExtentError, NoBoundsError, PluginError } from '@/core/exceptions/geoview-exceptions';
import { AbstractGVVectorTile } from '@/geo/layer/gv-layers/vector/abstract-gv-vector-tile';
import { AbstractBaseLayerEntryConfig } from '@/api/config/validation-classes/abstract-base-layer-entry-config';
import { VectorLayerEntryConfig } from '@/api/config/validation-classes/vector-layer-entry-config';
import { GroupLayerEntryConfig } from '@/api/config/validation-classes/group-layer-entry-config';
import type { TypeTimeSliderProps } from '@/core/stores/store-interface-and-intial-values/time-slider-state';
import { Fetch } from '@/core/utils/fetch-helper';
import { formatError } from '@/core/exceptions/core-exceptions';
import { LayerFilters } from '@/geo/layer/gv-layers/layer-filters';

// GV The paradigm when working with MapEventProcessor vs MapState goes like this:
// GV MapState provides: 'state values', 'actions' and 'setterActions'.
// GV Whereas Zustand would suggest having 'state values' and 'actions', in GeoView, we have a 'MapEventProcessor' in the middle.
// GV This is because we wanted to have centralized code between UI actions and backend actions via a MapEventProcessor.
// GV In summary:
// GV The UI components should use MapState's 'state values' to read and 'actions' to set states (which simply redirect to MapEventProcessor).
// GV The back-end code should use MapEventProcessor which uses 'state values' and 'setterActions'
// GV Essentially 3 main call-stacks:
// GV   - MapEventProcessor ---calls---> MapState.setterActions
// GV   - UI Component ---calls---> MapState.actions ---calls---> MapEventProcessor ---calls---> MapState.setterActions
// GV   - MapEventProcessor ---triggers---> MapViewer events ---calls---> MapState.setterActions
// GV The reason for this pattern is so that UI components and processes performing back-end code
// GV both end up running code in MapEventProcessor (UI: via 'actions' and back-end code via 'MapEventProcessor')
export class MapEventProcessor extends AbstractEventProcessor {
  /** The minimal delay to wait for the zoom, to be sure.. */
  static readonly ZOOM_MIN_DELAY = 500;

  /**
   * Initializes the map controls
   * @param {string} mapId - The map id being initialized
   */
  static async initMapControls(mapId: string): Promise<void> {
    // Log
    logger.logTraceCore('MAP EVENT PROCESSOR - initMapControls', mapId);

    // use api to access map because this function will set map element in store
    const mapViewer = this.getMapViewer(mapId);
    const { map } = mapViewer;
    const store = getGeoViewStore(mapId);

    // Add map controls (scale)
    const scaleBarMetric = new ScaleLine({
      units: 'metric',
      target: document.getElementById(`${mapId}-scaleControlBarMetric`) as HTMLElement,
      bar: true,
      text: true,
    });

    const scaleBarImperial = new ScaleLine({
      units: 'imperial',
      target: document.getElementById(`${mapId}-scaleControlBarImperial`) as HTMLElement,
      bar: true,
      text: true,
    });

    map.addControl(scaleBarMetric);
    map.addControl(scaleBarImperial);

    // Get the projection
    const mapProjection = Projection.getProjectionFromString(`EPSG:${store.getState().mapState.currentProjection}`);

    // add map overlays
    // create overlay for north pole icon
    const northPoleId = `${mapId}-northpole`;
    const projectionPosition = Projection.transformFromLonLat([NORTH_POLE_POSITION[1], NORTH_POLE_POSITION[0]], mapProjection);

    const northPoleMarker = new Overlay({
      id: northPoleId,
      position: projectionPosition,
      positioning: 'center-center',
      element: document.getElementById(northPoleId) as HTMLElement,
      stopEvent: false,
    });
    map.addOverlay(northPoleMarker);

    // create overlay for click marker icon
    const clickMarkerId = `${mapId}-clickmarker`;
    const clickMarkerOverlay = new Overlay({
      id: clickMarkerId,
      position: [-1, -1],
      positioning: 'center-center',
      offset: [-18, -30],
      element: document.getElementById(clickMarkerId) as HTMLElement,
      stopEvent: false,
    });
    map.addOverlay(clickMarkerOverlay);

    // Save in the store
    store.getState().mapState.setterActions.setOverlayNorthMarker(northPoleMarker);
    store.getState().mapState.setterActions.setOverlayClickMarker(clickMarkerOverlay);

    // Get the size
    const size = await mapViewer.getMapSize();

    // Set map size
    MapEventProcessor.setMapSize(mapId, size);

    // Get the scale information
    const scale = this.getScaleInfoFromDomElement(mapId);

    // Set the map scale
    MapEventProcessor.setMapScale(mapId, scale);

    // set map interaction
    this.setInteraction(mapId, store.getState().mapState.interaction);
  }

  // #region
  /**
   * Shortcut to get the Map state for a given map id
   * @param {string} mapId - map Id
   * @returns {IMapState} The Map state
   * @static
   */
  protected static getMapStateProtected(mapId: string): IMapState {
    // TODO: REFACTOR - Rename this function when we want to clarify the small confusion with getMapState function below
    // Return the map state
    return this.getState(mapId).mapState;
  }

  /**
   * Shortcut to get the Map Viewer instance for a given map id
   * This is use to reduce the use of api.getMapViewer(mapId) and be more explicit
   * @param {string} mapId - map Id
   * @returns {MapViewer} The Map viewer instance
   * @static
   */
  // TODO: REFACTOR? - These functions are the only place in the code where we still import api from '@/app'.
  static getMapViewer(mapId: string): MapViewer {
    return api.getMapViewer(mapId);
  }

  /**
   * Shortcut to get the Map Viewer layer api instance for a given map id
   * This is use to reduce the use of api.getMapViewer(mapId).layer and be more explicit
   * @param {string} mapId - map Id
   * @returns {LayerApi} The Map viewer layer API instance
   * @static
   */
  // TODO: REFACTOR? - These functions are the only place in the code where we still import api from '@/app'.
  static getMapViewerLayerAPI(mapId: string): LayerApi {
    return api.getMapViewer(mapId).layer;
  }

  /**
   * Shortcut to get the Map Viewer plugins instance for a given map id
   * This is use to reduce the use of api.getMapViewer(mapId).plugins and be more explicit
   * @param {string} mapId - map Id
   * @returns {PluginsContainer} The map plugins container
   * @static
   */
  // TODO: REFACTOR? - These functions are the only place in the code where we still import api from '@/app'.
  static async getMapViewerPlugins(mapId: string): Promise<PluginsContainer> {
    await whenThisThen(() => api && api.hasMapViewer(mapId));
    return api.getMapViewer(mapId).plugins;
  }

  /**
   * Forces the map to re-render all layers and features.
   * Useful when layer styles or features have been updated programmatically and need to be reflected visually.
   * @param {string} mapId - The map identifier
   * @static
   */
  static forceMapToRender(mapId: string): void {
    this.getMapViewer(mapId).map.render();
  }

  /**
   * Retrieves a plugin instance registered for a given map viewer, if it exists.
   * @param {string} mapId - The identifier of the map viewer.
   * @param {string} pluginId - The identifier of the plugin to retrieve.
   * @returns {Promise<AbstractPlugin | undefined>} A promise that resolves to the plugin instance if found, or `undefined` otherwise.
   * @static
   */
  static async getMapViewerPluginIfExists(mapId: string, pluginId: string): Promise<AbstractPlugin | undefined> {
    // Get the plugins
    const plugins = await this.getMapViewerPlugins(mapId);

    // If plugin exists
    if (plugins[pluginId]) {
      // Return it
      return plugins[pluginId];
    }

    // Not found
    return undefined;
  }

  /**
   * Gets the layer configuration of the specified layer path. This function redirects to the layer api.
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The layer path.
   * @returns {ConfigBaseClass | undefined} The layer configuration or undefined if not found.
   * @static
   */
  static getLayerEntryConfigIfExists(mapId: string, layerPath: string): ConfigBaseClass | undefined {
    // Redirect to the layer api
    return this.getMapViewerLayerAPI(mapId).getLayerEntryConfigIfExists(layerPath);
  }

  /**
   * Retrieves the scale information from the DOM elements for the given map ID.
   * @param {string} mapId - The unique identifier of the map.
   * @returns {TypeScaleInfo} The scale information object
   * @static
   */
  static getScaleInfoFromDomElement(mapId: string): TypeScaleInfo {
    // Get metric values
    const scaleControlBarMetric = document.getElementById(`${mapId}-scaleControlBarMetric`);
    const lineWidthMetric = (scaleControlBarMetric?.querySelector('.ol-scale-bar-inner') as HTMLElement)?.style.width;
    const labelGraphicMetric = (scaleControlBarMetric?.querySelector('.ol-scale-bar-inner')?.lastChild as HTMLElement)?.innerHTML;

    // Get metric values
    const scaleControlBarImperial = document.getElementById(`${mapId}-scaleControlBarImperial`);
    const lineWidthImperial = (scaleControlBarImperial?.querySelector('.ol-scale-bar-inner') as HTMLElement)?.style.width;
    const labelGraphicImperial = (scaleControlBarImperial?.querySelector('.ol-scale-bar-inner')?.lastChild as HTMLElement)?.innerHTML;

    // get resolution value (same for metric and imperial)
    const labelNumeric = (scaleControlBarMetric?.querySelector('.ol-scale-text') as HTMLElement)?.innerHTML;

    return { lineWidthMetric, labelGraphicMetric, lineWidthImperial, labelGraphicImperial, labelNumeric };
  }

  /**
   * Shortcut to get the Map config for a given map id
   * @param {string} mapId - The map id to retrieve the config for
   * @returns {TypeMapFeaturesConfig | undefined} the map config or undefined if there is no config for this map id
   * @static
   */
  static getGeoViewMapConfig(mapId: string): TypeMapFeaturesConfig | undefined {
    // Return the map config
    return this.getState(mapId).mapConfig;
  }

  static getBasemapOptions(mapId: string): TypeBasemapOptions {
    return this.getMapStateProtected(mapId).currentBasemapOptions || this.getMapStateProtected(mapId).basemapOptions;
  }

  static getCurrentBasemapOptions(mapId: string): TypeBasemapOptions {
    return this.getMapStateProtected(mapId).currentBasemapOptions;
  }

  /**
   * Gets initial filter(s) for a layer.
   * @param {string} mapId - The map id of the state to act on
   * @param {string} layerPath - The path of the layer
   * @returns {string | undefined} The initial filter(s) for the layer
   * @static
   */
  static getInitialFilter(mapId: string, layerPath: string): string | undefined {
    return this.getMapStateProtected(mapId).initialFilters[layerPath];
  }

  static getPointMarkers(mapId: string): Record<string, TypePointMarker[]> {
    return this.getMapStateProtected(mapId).pointMarkers;
  }

  /**
   * Gets geolocator search area
   * @param {string} mapId - The mapId
   * @returns {{ coords: Coordinate; bbox?: Extent } | undefined} The geolocator search area with coordinates and optional bounding box
   * @static
   */
  static getGeolocatorSearchArea(mapId: string): { coords: Coordinate; bbox?: Extent } | undefined {
    return this.getMapStateProtected(mapId).geolocatorSearchArea;
  }

  /**
   * Gets feature highlight color.
   * @param {string} mapId - The ID of the map
   * @returns {TypeHighlightColors} The highlight color
   * @static
   */
  static getFeatureHighlightColor(mapId: string): TypeHighlightColors {
    return this.getMapStateProtected(mapId).featureHighlightColor;
  }

  static clickMarkerIconShow(mapId: string, marker: TypeClickMarker): void {
    // Project coords
    const projectedCoords = Projection.transformPoints(
      [marker.lonlat],
      Projection.PROJECTION_NAMES.LONLAT,
      `EPSG:${this.getMapStateProtected(mapId).currentProjection}`
    );

    // Redirect to processor
    this.setClickMarkerOnPosition(mapId, projectedCoords[0]);

    // Save in store
    this.getMapStateProtected(mapId).setterActions.setClickMarker(projectedCoords[0]);
  }

  static clickMarkerIconHide(mapId: string): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setClickMarker(undefined);
  }

  static highlightBBox(mapId: string, extent: Extent, isLayerHighlight?: boolean): void {
    // Perform a highlight
    this.getMapViewerLayerAPI(mapId).featureHighlight.highlightGeolocatorBBox(extent, isLayerHighlight);
  }

  static getMapInteraction(mapId: string): TypeInteraction {
    return this.getMapStateProtected(mapId).interaction;
  }

  /**
   * Gets map layer paths in order.
   * @param {string} mapId - The map id
   * @returns {string[]} The ordered layer paths
   */
  static getMapLayerOrder(mapId: string): string[] {
    return this.getMapStateProtected(mapId).orderedLayerInfo.map((orderedLayerInfo) => {
      return orderedLayerInfo.layerPath;
    });
  }

  /**
   * Gets the status of a layer.
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The layer path.
   * @returns {TypeLayerStatus | undefined} The layer status
   * @static
   */
  static getMapLayerStatus(mapId: string, layerPath: string): TypeLayerStatus | undefined {
    return LegendEventProcessor.getLegendLayerInfo(mapId, layerPath)?.layerStatus;
  }

  static getMapState(mapId: string): TypeMapState {
    const mapState = this.getMapStateProtected(mapId);
    return {
      currentProjection: mapState.currentProjection,
      currentZoom: mapState.zoom,
      mapCenterCoordinates: mapState.centerCoordinates,
      mapExtent: mapState.mapExtent!,
      rotation: mapState.rotation,
      pointerPosition: mapState.pointerPosition || {
        pixel: [],
        lonlat: [],
        projected: [],
        dragging: false,
      },
      singleClickedPosition: mapState.clickCoordinates || {
        pixel: [],
        lonlat: [],
        projected: [],
        dragging: false,
      },
    };
  }

  /**
   * Gets the map state information for creating the export layout
   * @param {string} mapId - The map id
   * @returns {TypeMapStateForExportLayout} The map state required for the export layout
   */
  static getMapStateForExportLayout(mapId: string): TypeMapStateForExportLayout {
    const mapState = this.getMapStateProtected(mapId);
    return {
      attribution: mapState.attribution,
      northArrow: mapState.northArrow,
      northArrowElement: mapState.northArrowElement,
      mapScale: mapState.scale,
      mapRotation: mapState.rotation,
      currentProjection: mapState.currentProjection,
    };
  }

  static setMapAttribution(mapId: string, attribution: string[]): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setAttribution(attribution);
  }

  static setMapLoaded(mapId: string, mapLoaded: boolean): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setMapLoaded(mapLoaded);
  }

  static setMapDisplayed(mapId: string): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setMapDisplayed();
  }

  static setMapPointerPosition(mapId: string, pointerPosition: TypeMapMouseInfo): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setPointerPosition(pointerPosition);
  }

  static setClickCoordinates(mapId: string, clickCoordinates: TypeMapMouseInfo): void {
    // GV: We do not need to perform query, there is a handler on the map click in layer set.
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setClickCoordinates(clickCoordinates);

    // If in WCAG mode, we need to emit the event
    if (getAppCrosshairsActive(mapId)) this.getMapViewer(mapId).emitMapSingleClick(clickCoordinates);
  }

  static getLayersInVisibleRange = (mapId: string): string[] => {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    const layersInVisibleRange = orderedLayerInfo.filter((layer) => layer.inVisibleRange).map((layer) => layer.layerPath);
    return layersInVisibleRange;
  };

  static setLayerInVisibleRange(mapId: string, layerPath: string, inVisibleRange: boolean): void {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    const orderedLayer = orderedLayerInfo.find((layer) => layer.layerPath === layerPath);

    if (orderedLayer && orderedLayer.inVisibleRange !== inVisibleRange) {
      orderedLayer.inVisibleRange = inVisibleRange;
      this.setMapOrderedLayerInfo(mapId, orderedLayerInfo);
    }
  }

  static setZoom(mapId: string, zoom: number): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setZoom(zoom);
  }

  static setIsMouseInsideMap(mapId: string, inside: boolean): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setIsMouseInsideMap(inside);
  }

  static setRotation(mapId: string, rotation: number): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setRotation(rotation);
  }

  static setMapSize(mapId: string, size: Size, resizeMap: boolean = false): void {
    // Save in store
    if (resizeMap) this.getMapViewer(mapId).map.setSize(size);
    this.getMapStateProtected(mapId).setterActions.setMapSize(size);
  }

  static setMapScale(mapId: string, scale: TypeScaleInfo): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setMapScale(scale);
  }

  static setMapMoveEnd(
    mapId: string,
    centerCoordinates: Coordinate,
    pointerPosition: TypeMapMouseInfo,
    degreeRotation: string,
    isNorthVisible: boolean,
    mapExtent: Extent,
    scale: TypeScaleInfo
  ): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setMapMoveEnd(
      centerCoordinates,
      pointerPosition,
      degreeRotation,
      isNorthVisible,
      mapExtent,
      scale
    );
  }

  static setInteraction(mapId: string, interaction: TypeInteraction): void {
    // enable or disable map interaction when type of map interaction is set
    this.getMapViewer(mapId)
      .map.getInteractions()
      .forEach((x) => x.setActive(interaction === 'dynamic'));

    // Save in store
    this.getMapStateProtected(mapId).setterActions.setInteraction(interaction);
  }

  static async setProjection(mapId: string, projectionCode: TypeValidMapProjectionCodes): Promise<void> {
    try {
      // Set circular progress to hide basemap switching
      AppEventProcessor.setCircularProgress(mapId, true);

      // get view status (center and projection) to calculate new center
      const currentView = this.getMapViewer(mapId).map.getView();
      const currentCenter = currentView.getCenter();
      const currentProjection = currentView.getProjection().getCode();
      const centerLatLng = Projection.transformPoints([currentCenter!], currentProjection, Projection.PROJECTION_NAMES.LONLAT)[0] as [
        number,
        number,
      ];
      const newProjection = projectionCode;

      // If maxExtent was provided and in the native projection, apply
      // GV The extent is different between LCC and WM and switching from one to the other may introduce weird constraint.
      // GV We may have to keep extent as array for configuration file but, technically, user does not change projection often.
      // GV A wider LCC extent like [-125, 30, -60, 89] (minus -125) will introduce distortion on larger screen...
      // GV It is why we apply the max extent only on native projection, otherwise we apply default
      const viewSettings = this.getGeoViewMapConfig(mapId)?.map.viewSettings;
      const mapMaxExtent =
        viewSettings!.maxExtent && Number(newProjection) === Number(viewSettings!.projection)
          ? viewSettings?.maxExtent
          : MAX_EXTENTS_RESTRICTION[newProjection];

      // create new view settings
      const newView: TypeViewSettings = {
        initialView: { zoomAndCenter: [currentView.getZoom() as number, centerLatLng] },
        minZoom: currentView.getMinZoom(),
        maxZoom: currentView.getMaxZoom(),
        maxExtent: mapMaxExtent,
        projection: newProjection,
      };

      // use store action to set projection value in store and apply new view to the map
      this.getMapStateProtected(mapId).setterActions.setProjection(projectionCode);

      // Clear the WMS layers that had an override CRS
      this.getMapViewerLayerAPI(mapId).clearWMSLayersWithOverrideCRS();

      // Clear any loaded vector features in the data table
      this.getMapViewerLayerAPI(mapId).clearVectorFeaturesFromAllFeatureInfoLayerSet();

      // Before changing the view, clear the basemaps right away to prevent a moment where a
      // vector tile basemap might, momentarily, be in different projection than the view.
      // Note: It seems that since OpenLayers 10.5 OpenLayers throws an exception about this. So this line was added.
      this.getMapViewer(mapId).basemap.clearBasemaps();

      // Set overview map visibility to false when reproject to remove it from the map as it is vector tile
      MapEventProcessor.setOverviewMapVisibility(mapId, false);

      // Remove all vector tiles from the map, because they don't allow on-the-fly reprojection (OpenLayers 10.5 exception issue)
      // GV Experimental code, to test further... not problematic to keep it for now
      this.getMapViewerLayerAPI(mapId)
        .getGeoviewLayers()
        .filter((layer) => layer instanceof AbstractGVVectorTile)
        .forEach((layer) => {
          // Remove the layer
          this.getMapViewerLayerAPI(mapId).removeLayerUsingPath(layer.getLayerPath());

          // Log
          this.getMapViewer(mapId).notifications.showWarning('warning.layer.vectorTileRemoved', [layer.getLayerName()], true);
        });

      // set new view
      this.getMapViewer(mapId).setView(newView);

      // reload the basemap from new projection
      await this.resetBasemap(mapId);

      // refresh layers so new projection is render properly
      this.getMapViewerLayerAPI(mapId).refreshLayers();

      // Remove layer highlight if present to avoid bad reprojection
      const highlightName = LegendEventProcessor.getLayerPanelState(mapId, 'highlightedLayer') as string;
      if (highlightName !== '') {
        MapEventProcessor.changeOrRemoveLayerHighlight(mapId, highlightName, highlightName);
      }

      // Reset the map object of overview map control
      MapEventProcessor.setOverviewMapVisibility(mapId, true);

      // Repeat last query for layer features after a delay to allow projection change to propagate
      this.getMapViewer(mapId)
        .layer.repeatLastQueryIfAny()
        .catch((error: unknown) => {
          // Log
          logger.logPromiseFailed('in repeatLastQueryIfAny in MapEventProcessor.setProjection', error);
        });
    } finally {
      // Remove circular progress as refresh is done
      AppEventProcessor.setCircularProgress(mapId, false);
    }
  }

  /**
   * Sets the home view
   * @param mapId - The map id
   * @param view - The view settings
   * @returns {void}
   * @static
   */
  static setHomeButtonView(mapId: string, view: TypeMapViewSettings): void {
    // Save in store
    this.getMapStateProtected(mapId).setterActions.setHomeView(view);
  }

  static rotate(mapId: string, rotation: number): void {
    // Do the actual view map rotation
    this.getMapViewer(mapId).map.getView().animate({ rotation });
    // GV No need to save in the store, because this will trigger an event on MapViewer which will take care of updating the store
  }

  static zoomMap(mapId: string, zoom: number, duration: number = OL_ZOOM_DURATION): Promise<void> {
    // Do the actual zoom
    this.getMapViewer(mapId).map.getView().animate({ zoom, duration });

    // Use a Promise and resolve it when the duration expired
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, duration + MapEventProcessor.ZOOM_MIN_DELAY);
    });
    // GV No need to save in the store, because this will trigger an event on MapViewer which will take care of updating the store
  }

  /**
   * Gets the ordered layer info.
   * @param {string} mapId - The map id
   * @returns {TypeOrderedLayerInfo[]} The ordered layer info
   * @static
   */
  static getMapOrderedLayerInfo(mapId: string): TypeOrderedLayerInfo[] {
    return this.getMapStateProtected(mapId).orderedLayerInfo;
  }

  /**
   * Gets the ordered layer info for one layer.
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The path of the layer to get.
   * @returns {TypeOrderedLayerInfo | undefined} The ordered layer info.
   * @static
   */
  static findMapLayerFromOrderedInfo(
    mapId: string,
    layerPath: string,
    orderedLayerInfo: TypeOrderedLayerInfo[] = this.getMapStateProtected(mapId).orderedLayerInfo
  ): TypeOrderedLayerInfo | undefined {
    return orderedLayerInfo.find((layer) => layer.layerPath === layerPath);
  }

  /**
   * Gets the ordered layer info for one layer and its children.
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The path of the layer to get.
   * @param {TypeOrderedLayerInfo[]} orderedLayerInfo - The array of ordered layer info to search, default is current ordered layer info.
   * @returns {TypeOrderedLayerInfo[] | undefined} The ordered layer info of the layer and its children.
   * @static
   */
  static findMapLayerAndChildrenFromOrderedInfo(
    mapId: string,
    layerPath: string,
    orderedLayerInfo: TypeOrderedLayerInfo[] = this.getMapStateProtected(mapId).orderedLayerInfo
  ): TypeOrderedLayerInfo[] {
    return orderedLayerInfo.filter((layer) => layer.layerPath.startsWith(`${layerPath}/`) || layer.layerPath === layerPath);
  }

  static getMapIndexFromOrderedLayerInfo(mapId: string, layerPath: string): number {
    // Get index of a layer
    const info = this.getMapStateProtected(mapId).orderedLayerInfo;
    for (let i = 0; i < info.length; i++) if (info[i].layerPath === layerPath) return i;
    return -1;
  }

  static getLegendCollapsibleLayers(mapId: string): TypeOrderedLayerInfo[] {
    // Get collapsible layers
    const orderedLayerInfo = this.getMapOrderedLayerInfo(mapId);
    const { legendLayers } = this.getState(mapId).layerState;

    return orderedLayerInfo.filter((layer) => {
      const legendLayer = LegendEventProcessor.findLayerByPath(legendLayers, layer.layerPath);
      return (
        (legendLayer?.children && legendLayer.children.length > 0) ||
        (legendLayer?.items && legendLayer.items.length > 1) ||
        (legendLayer?.schemaTag === CONST_LAYER_TYPES.WMS &&
          legendLayer?.icons?.some((icon) => icon.iconImage && icon.iconImage !== 'no data'))
      );
    });
  }

  static getAllLegendLayersCollapsed(mapId: string): boolean {
    // Get whether all the collapsible layers are collapsed
    const collapsibleLayers = MapEventProcessor.getLegendCollapsibleLayers(mapId);

    // If there are no collapsible layers, return true
    if (collapsibleLayers.length === 0) return true;
    return collapsibleLayers.every((layer) => layer.legendCollapsed);
  }

  static getMapLegendCollapsedFromOrderedLayerInfo(mapId: string, layerPath: string): boolean {
    // Get legend status of a layer
    return this.findMapLayerFromOrderedInfo(mapId, layerPath)?.legendCollapsed !== false;
  }

  static getMapVisibilityFromOrderedLayerInfo(mapId: string, layerPath: string): boolean | undefined {
    // Get visibility of a layer
    return this.findMapLayerFromOrderedInfo(mapId, layerPath)?.visible;
  }

  /**
   * Checks if any parent of a layer is hidden.
   * @param {string} mapId - The ID of the map.
   * @param {string} layerPath - The layer path of the layer to check.
   * @static
   */
  static getMapLayerParentHidden(mapId: string, layerPath: string): boolean {
    const curOrderedLayerInfo = MapEventProcessor.getMapOrderedLayerInfo(mapId);

    // For each parent
    const parentLayerPathArray = layerPath.split('/');
    parentLayerPathArray.pop();
    let parentLayerPath = parentLayerPathArray.join('/');
    let parentLayerInfo = curOrderedLayerInfo.find((info: TypeOrderedLayerInfo) => info.layerPath === parentLayerPath);

    while (parentLayerInfo !== undefined) {
      // Return true as soon as any parent is not visible
      if (parentLayerInfo.visible === false) return true;

      // Prepare for next parent
      parentLayerPathArray.pop();
      parentLayerPath = parentLayerPathArray.join('/');
      // eslint-disable-next-line no-loop-func
      parentLayerInfo = curOrderedLayerInfo.find((info: TypeOrderedLayerInfo) => info.layerPath === parentLayerPath);
    }

    return false;
  }

  static getMapInVisibleRangeFromOrderedLayerInfo(mapId: string, layerPath: string): boolean {
    // Get inVisibleRange of a layer
    return this.findMapLayerFromOrderedInfo(mapId, layerPath)?.inVisibleRange !== false;
  }

  static addHighlightedFeature(mapId: string, feature: TypeFeatureInfoEntry): void {
    if (feature.geoviewLayerType !== CONST_LAYER_TYPES.WMS) {
      MapEventProcessor.getMapViewerLayerAPI(mapId).featureHighlight.highlightFeature(feature);
      // Save in store
      this.getMapStateProtected(mapId).setterActions.setHighlightedFeatures([
        ...this.getMapStateProtected(mapId).highlightedFeatures,
        feature,
      ]);
    }
  }

  static removeHighlightedFeature(mapId: string, feature: TypeFeatureInfoEntry | 'all'): void {
    if (feature === 'all' || feature.geoviewLayerType !== CONST_LAYER_TYPES.WMS) {
      // Filter what we want to keep as highlighted features
      let highlightedFeatures: TypeFeatureInfoEntry[] = [];
      if (feature === 'all') {
        MapEventProcessor.getMapViewerLayerAPI(mapId).featureHighlight.removeHighlight(feature);
      } else {
        MapEventProcessor.getMapViewerLayerAPI(mapId).featureHighlight.removeHighlight(feature.uid!);
        highlightedFeatures = this.getMapStateProtected(mapId).highlightedFeatures.filter(
          (featureInfoEntry: TypeFeatureInfoEntry) => featureInfoEntry.uid !== feature.uid
        );
      }

      // Save in store
      this.getMapStateProtected(mapId).setterActions.setHighlightedFeatures(highlightedFeatures);
    }
  }

  static removeLayerHighlights(mapId: string, layerPath: string): void {
    // Redirect to layer api
    MapEventProcessor.getMapViewerLayerAPI(mapId).removeLayerHighlights(layerPath);
  }

  /**
   * Adds a layer to the map. This methods redirects to the method on the layer api.
   * @param {string} mapId - The map id.
   * @param geoviewLayerConfig - The geoview layer configuration to add.
   * @param abortSignal - Optional {@link AbortSignal} used to cancel the layer creation process.
   * @returns The result of the addition of the geoview layer.
   * @throws {LayerCreatedTwiceError} When there already is a layer on the map with the provided geoviewLayerId.
   * The result contains the instanciated GeoViewLayer along with a promise that will resolve when the layer will be officially on the map.
   * @static
   */
  static addGeoviewLayer(mapId: string, geoviewLayerConfig: TypeGeoviewLayerConfig, abortSignal?: AbortSignal): GeoViewLayerAddedResult {
    // Redirect to layer api
    return MapEventProcessor.getMapViewerLayerAPI(mapId).addGeoviewLayer(geoviewLayerConfig, abortSignal);
  }

  /**
   * Add a point marker
   * @param {string} mapId - The ID of the map.
   * @param {string} group - The group to add the markers to.
   * @param {TypePointMarker} pointMarkers - The point markers to add.
   * @static
   */
  static addPointMarkers(mapId: string, group: string, pointMarkers: TypePointMarker[]): void {
    const curMarkers = this.getMapStateProtected(mapId).pointMarkers;

    // Check for existing group, and existing markers that match input IDs or coordinates
    let groupMarkers = curMarkers[group];
    if (groupMarkers) {
      pointMarkers.forEach((pointMarker) => {
        // Replace any existing ids or markers at the same coordinates with new marker
        groupMarkers = groupMarkers.filter((marker) => marker.coordinate.join() !== pointMarker.coordinate.join());
        groupMarkers = groupMarkers.filter((marker) => marker.id !== pointMarker.id);
        groupMarkers.push(pointMarker);
      });
    } else {
      groupMarkers = pointMarkers;
    }

    // Set the group markers, and update on the map
    curMarkers[group] = groupMarkers;
    this.getMapStateProtected(mapId).setterActions.setPointMarkers(curMarkers);
    MapEventProcessor.getMapViewerLayerAPI(mapId).featureHighlight.pointMarkers?.updatePointMarkers(curMarkers);
  }

  /**
   * Remove a point marker
   * @param {string} mapId - The ID of the map.
   * @param {string} group - The group to remove the markers from.
   * @param {string | Coordinate} idsOrCoordinates - The IDs or coordinates of the markers to remove.
   * @static
   */
  static removePointMarkersOrGroup(mapId: string, group: string, idsOrCoordinates?: string[] | Coordinate[]): void {
    const curMarkers = this.getMapStateProtected(mapId).pointMarkers;

    // If no IDs or coordinates are provided, remove group
    if (!idsOrCoordinates) {
      delete curMarkers[group];
    } else {
      // Set property to check
      const property = typeof idsOrCoordinates[0] === 'string' ? 'id' : 'coordinate';

      // Filter out markers that match given ones
      let groupMarkers = curMarkers[group];
      idsOrCoordinates.forEach((idOrCoordinate) => {
        groupMarkers = groupMarkers.filter((marker) => marker[property] !== idOrCoordinate);
      });

      curMarkers[group] = groupMarkers;
    }

    // Set the pointMarkers and update on map
    this.getMapStateProtected(mapId).setterActions.setPointMarkers(curMarkers);
    MapEventProcessor.getMapViewerLayerAPI(mapId).featureHighlight.pointMarkers?.updatePointMarkers(curMarkers);
  }

  /**
   * Update or remove the layer highlight.
   * @param {string} mapId - The ID of the map.
   * @param {string} layerPath - The layer path to set as the highlighted layer.
   * @param {string} highlightedLayerPath - The layer path of the currently highlighted layer.
   * @returns {string} The layer path of the highlighted layer.
   * @static
   */
  static changeOrRemoveLayerHighlight(mapId: string, layerPath: string, highlightedLayerPath: string): string {
    // If layer is currently highlighted layer, remove highlight
    if (highlightedLayerPath === layerPath) {
      LegendEventProcessor.setHighlightLayer(mapId, '');
      MapEventProcessor.getMapViewerLayerAPI(mapId).removeHighlightLayer();
      return '';
    }

    // If the layer path is set to nothing, done
    if (!layerPath) return '';

    // Redirect to layer to highlight
    MapEventProcessor.getMapViewerLayerAPI(mapId).highlightLayer(layerPath);

    // Get bounds and highlight a bounding box for the layer (if true in global settings)
    const bounds = LegendEventProcessor.getLayerBounds(mapId, layerPath);
    if (bounds && AppEventProcessor.getShowLayerHighlightLayerBbox(mapId))
      this.getMapStateProtected(mapId).actions.highlightBBox(bounds, true);

    return layerPath;
  }

  static addInitialFilter(mapId: string, layerPath: string, filter: string): void {
    const curFilters = this.getMapStateProtected(mapId).initialFilters;
    this.getMapStateProtected(mapId).setterActions.setInitialFilters({ ...curFilters, [layerPath]: filter });
  }

  static setCurrentBasemapOptions(mapId: string, basemapOptions: TypeBasemapOptions): void {
    this.getMapStateProtected(mapId).setterActions.setCurrentBasemapOptions(basemapOptions);
  }

  static getMapHoverFeatureInfo(mapId: string): TypeHoverFeatureInfo | undefined | null {
    return this.getMapStateProtected(mapId).hoverFeatureInfo;
  }

  static setMapLayerHoverable(mapId: string, layerPath: string, hoverable: boolean): void {
    this.getMapStateProtected(mapId).setterActions.setHoverable(layerPath, hoverable);
  }

  static setMapHoverFeatureInfo(mapId: string, hoverFeatureInfo: TypeHoverFeatureInfo): void {
    this.getMapStateProtected(mapId).setterActions.setHoverFeatureInfo(hoverFeatureInfo);
  }

  static setMapOrderedLayerInfo(mapId: string, orderedLayerInfo: TypeOrderedLayerInfo[]): void {
    this.getMapStateProtected(mapId).setterActions.setOrderedLayerInfo(orderedLayerInfo);
    this.setLayerZIndices(mapId);
  }

  static setMapLayerQueryable(mapId: string, layerPath: string, queryable: boolean): void {
    this.getMapStateProtected(mapId).setterActions.setQueryable(layerPath, queryable);
  }

  static setMapLegendCollapsed(mapId: string, layerPath: string, collapsed: boolean): void {
    this.getMapStateProtected(mapId).setterActions.setLegendCollapsed(layerPath, collapsed);
  }

  static setAllMapLayerCollapsed(mapId: string, newCollapsed: boolean): void {
    // Set the collapsed state for all layers
    const orderedLayerInfo = MapEventProcessor.getMapOrderedLayerInfo(mapId);
    orderedLayerInfo.forEach((layer) => {
      if (layer.legendCollapsed !== newCollapsed) {
        this.setMapLegendCollapsed(mapId, layer.layerPath, newCollapsed);
      }
    });
  }

  /**
   * Sets or toggles the visibility of a specific layer within a map.
   * If the layer exists at the provided layer path for the given map, the method delegates
   * the visibility change to the map viewer's layer API. If `newValue` is provided, the layer
   * visibility is explicitly set to that value; otherwise, the visibility is toggled.
   * @param {string} mapId - The identifier of the map containing the target layer.
   * @param {string} layerPath - The path of the layer whose visibility is being changed.
   * @param {boolean} [newValue] - Optional. The new visibility value. If omitted, the visibility is toggled.
   * @returns {boolean} The resulting visibility state of the layer after the operation, or `false`
   * if the layer does not exist at the given path.
   * @static
   */
  static setOrToggleMapLayerVisibility(mapId: string, layerPath: string, newValue?: boolean): boolean {
    // If the GV layer exists at the layer path
    if (this.getMapViewerLayerAPI(mapId).getGeoviewLayerIfExists(layerPath)) {
      // Redirect to layerAPI
      return this.getMapViewerLayerAPI(mapId).setOrToggleLayerVisibility(layerPath, newValue);
    }
    return false;
  }

  /**
   * Sets the opacity of a layer in the layer legend.
   * @param {string} mapId - The ID of the map.
   * @param {string} layerPath - The layer path of the layer to change.
   * @param {boolean} visibility - The visibility to set.
   * @returns {void}
   * @static
   */
  static setMapLayerVisibilityInStore(mapId: string, layerPath: string, visibility: boolean): void {
    const curOrderedLayerInfo = this.getMapOrderedLayerInfo(mapId);

    // Get and update ordered layer info
    const layerInfo = curOrderedLayerInfo.find((orderedLayerInfo) => orderedLayerInfo.layerPath === layerPath);
    if (layerInfo && layerInfo.visible !== visibility) {
      layerInfo.visible = visibility;
      // Update the store with setterActions
      this.setMapOrderedLayerInfo(mapId, curOrderedLayerInfo);
    }
  }

  /**
   * Sets the visibility of **all layers** in a given map.
   * Iterates through all GeoView layers associated with the specified map ID and
   * applies the provided visibility value. Only layers whose current visibility
   * differs from the desired state will be updated.
   * @param {string} mapId - The identifier of the map whose layers will be updated.
   * @param {boolean} newVisibility - The visibility state to apply to all layers (`true` to show, `false` to hide).
   * @returns {void}
   * @static
   */
  static setAllMapLayerVisibility(mapId: string, newVisibility: boolean): void {
    // Set the visibility for all layers
    const layerApi = this.getMapViewerLayerAPI(mapId);
    layerApi.getGeoviewLayers().forEach((layer) => {
      if (!layer.getLayerConfig().getGeoviewLayerConfig().useAsBasemap === true && layer.getVisible() !== newVisibility) {
        layerApi.setOrToggleLayerVisibility(layer.getLayerPath(), newVisibility);
      }
    });
  }

  /**
   * Sets the visibility of the Geoview basemap layer.
   * @param {string} mapId - The identifier of the map whose basemap layer will be updated.
   * @param {boolean} newVisibility - The visibility state to apply to the basemap layer (`true` to show, `false` to hide).
   * @returns {void}
   * @static
   */
  static setVisibilityOfGeoviewBasemapLayers(mapId: string, newVisibility: boolean): void {
    const layerApi = this.getMapViewerLayerAPI(mapId);
    layerApi.getGeoviewLayers().forEach((layer) => {
      if (layer.getLayerConfig().getGeoviewLayerConfig().useAsBasemap === true && layer.getVisible() !== newVisibility) {
        layerApi.setOrToggleLayerVisibility(layer.getLayerPath(), newVisibility);
      }
    });
  }

  // TODO: REFACTOR? - These functions are the only place in the code where we still import api from '@/app'.
  static reorderLayer(mapId: string, layerPath: string, move: number): void {
    // Redirect to state API
    api.getMapViewer(mapId).stateApi.reorderLayers(mapId, layerPath, move);
  }

  /**
   * Replace a layer in the orderedLayerInfo array.
   *
   * @param {string} mapId The ID of the map to add the layer to.
   * @param {ConfigBaseClass} layerConfig The config of the layer to add.
   * @param {string} layerPathToReplace The layerPath of the info to replace.
   * @returns {void}
   * @static
   */
  static replaceOrderedLayerInfo(mapId: string, layerConfig: ConfigBaseClass, layerPathToReplace?: string): void {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    const layerPath = layerConfig.getGeoviewLayerId() ? `${layerConfig.getGeoviewLayerId()}/base-group` : layerConfig.layerPath;
    const pathToSearch = layerPathToReplace || layerPath;
    const index = this.getMapIndexFromOrderedLayerInfo(mapId, pathToSearch);
    const replacedLayers = this.findMapLayerAndChildrenFromOrderedInfo(mapId, pathToSearch);
    const newOrderedLayerInfo = LayerApi.generateArrayOfLayerOrderInfo(layerConfig);
    orderedLayerInfo.splice(index, replacedLayers.length, ...newOrderedLayerInfo);

    // Redirect
    this.setMapOrderedLayerInfo(mapId, orderedLayerInfo);
  }

  /**
   * Add a new layer to the orderedLayerInfo array using a layer config.
   *
   * @param {string} mapId The ID of the map to add the layer to.
   * @param {TypeGeoviewLayerConfig} geoviewLayerConfig The config of the layer to add.
   * @returns {void}
   * @static
   */
  static addOrderedLayerInfoByConfig(
    mapId: string,
    geoviewLayerConfig: TypeGeoviewLayerConfig | TypeLayerEntryConfig,
    index?: number
  ): void {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    const newOrderedLayerInfo = LayerApi.generateArrayOfLayerOrderInfo(geoviewLayerConfig);
    if (!index) orderedLayerInfo.unshift(...newOrderedLayerInfo);
    else orderedLayerInfo.splice(index, 0, ...newOrderedLayerInfo);

    // Redirect
    this.setMapOrderedLayerInfo(mapId, orderedLayerInfo);
  }

  /**
   * Add new layer info to the orderedLayerInfo array.
   *
   * @param {string} mapId The ID of the map to add the layer to.
   * @param {TypeOrderedLayerInfo} layerInfo The ordered layer info to add.
   * @returns {void}
   * @static
   */
  static addOrderedLayerInfo(mapId: string, layerInfo: TypeOrderedLayerInfo, index?: number): void {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    if (!index) orderedLayerInfo.unshift(layerInfo);
    else orderedLayerInfo.splice(index, 0, layerInfo);

    // Redirect
    this.setMapOrderedLayerInfo(mapId, orderedLayerInfo);
  }

  /**
   * Remove a layer from the orderedLayerInfo array.
   *
   * @param {string} mapId The ID of the map to remove the layer from.
   * @param {string} layerPath - The path of the layer to remove.
   * @param {boolean} removeSublayers - Should sublayers be removed.
   * @returns {void}
   * @static
   */
  static removeOrderedLayerInfo(mapId: string, layerPath: string, removeSublayers: boolean = true): void {
    const { orderedLayerInfo } = this.getMapStateProtected(mapId);
    const newOrderedLayerInfo = removeSublayers
      ? orderedLayerInfo.filter((layerInfo) => !layerInfo.layerPath.startsWith(`${layerPath}/`) && !(layerInfo.layerPath === layerPath))
      : orderedLayerInfo.filter((layerInfo) => !(layerInfo.layerPath === layerPath));

    // Redirect
    this.setMapOrderedLayerInfo(mapId, newOrderedLayerInfo);
  }

  // #endregion

  // **********************************************************
  // Static functions for Store Map State to action on API
  // **********************************************************
  // GV NEVER add a store action who does set state AND map action at a same time.
  // GV Review the action in store state to make sure
  static getOverviewMapControl(mapId: string, div: HTMLDivElement): OLOverviewMap {
    const olMap = this.getMapViewer(mapId).map;
    return this.getMapViewer(mapId).basemap.getOverviewMapControl(olMap, div);
  }

  static setOverviewMapVisibility(mapId: string, visible: boolean): void {
    const olMap = this.getMapViewer(mapId).map;
    this.getMapViewer(mapId).basemap.setOverviewMapControlVisibility(olMap, visible);
  }

  static resetBasemap(mapId: string): Promise<void> {
    // reset basemap will use the current display language and projection and recreate the basemap
    const language = AppEventProcessor.getDisplayLanguage(mapId);
    const projection = this.getMapState(mapId).currentProjection as TypeValidMapProjectionCodes;
    return this.getMapViewer(mapId).basemap.loadDefaultBasemaps(projection, language);
  }

  static async setBasemap(mapId: string, basemapOptions: TypeBasemapOptions): Promise<void> {
    // Set basemap will use the current display language and projection and recreate the basemap
    const language = AppEventProcessor.getDisplayLanguage(mapId);
    const projection = this.getMapState(mapId).currentProjection as TypeValidMapProjectionCodes;

    // Create the core basemap
    const basemap = await this.getMapViewer(mapId).basemap.createCoreBasemap(basemapOptions, projection, language);

    // Set the basemap and basemap options
    this.getMapViewer(mapId).basemap.setBasemap(basemap);
    this.setCurrentBasemapOptions(mapId, basemapOptions);
  }

  static setMapKeyboardPanInteractions(mapId: string, panDelta: number): void {
    const mapElement = this.getMapViewer(mapId).map;

    // replace the KeyboardPan interaction by a new one
    mapElement.getInteractions().forEach((interactionItem) => {
      if (interactionItem instanceof KeyboardPan) {
        mapElement.removeInteraction(interactionItem);
      }
    });
    mapElement.addInteraction(new KeyboardPan({ pixelDelta: panDelta }));
  }

  static setActiveMapInteractionWCAG(mapId: string, active: boolean): void {
    const mapElement = this.getMapViewer(mapId).map;

    // replace the KeyboardPan interaction by a new one
    mapElement.getInteractions().forEach((interactionItem) => {
      if (interactionItem instanceof KeyboardPan) interactionItem.setActive(active);
      if (interactionItem instanceof KeyboardZoom) interactionItem.setActive(active);
    });
  }

  /**
   * Set the React root overview map element so it can be destroy if the map element is destroyed
   *
   * @param mapId The map id.
   * @param overviewRoot The React root element for the overview map
   * @returns {void}
   * @static
   */
  static setMapOverviewMapRoot(mapId: string, overviewRoot: Root): void {
    this.getMapViewer(mapId).overviewRoot = overviewRoot;
  }

  /**
   * Zoom to the specified extent.
   *
   * @param {string} mapId - The map id.
   * @param {Extent} extent - The extent to zoom to.
   * @param {FitOptions} options - The options to configure the zoomToExtent (default: { padding: [100, 100, 100, 100], maxZoom: 13, duration: 500 }).
   * @returns Promise<void>
   * @static
   */
  static zoomToExtent(mapId: string, extent: Extent, options: FitOptions = DEFAULT_OL_FITOPTIONS): Promise<void> {
    // Merge user options with defaults
    const mergedOptions: FitOptions = { ...DEFAULT_OL_FITOPTIONS, ...options };

    // Validate the extent coordinates - need to make sure we aren't excluding zero with !number or using invalid extents
    const validatedExtent = GeoUtilities.validateExtent(extent, `EPSG:${this.getMapStateProtected(mapId).currentProjection.toString()}`);
    if (
      !extent.some((number) => {
        return (!number && number !== 0) || Number.isNaN(number);
      }) &&
      JSON.stringify(extent) === JSON.stringify(validatedExtent)
    ) {
      // Store state will be updated by map event
      this.getMapViewer(mapId).getView().fit(extent, mergedOptions);

      // Wait a bit and return.
      return delay(mergedOptions.duration! + MapEventProcessor.ZOOM_MIN_DELAY);
    }

    // Invalid extent
    this.getMapViewer(mapId).notifications.showError('error.map.invalidZoomExtent', [], true);
    throw new InvalidExtentError(extent);
  }

  static async zoomToGeoLocatorLocation(mapId: string, searchItem: string, coords: Coordinate, bbox?: Extent): Promise<void> {
    // Set the map state store with the location use for the zoom to location
    this.getMapStateProtected(mapId).setterActions.setGeolocatorSearchArea({ searchItem, coords, bbox });

    const indicatorBox = document.getElementsByClassName('ol-overviewmap-box');
    for (let i = 0; i < indicatorBox.length; i++) {
      (indicatorBox[i] as HTMLElement).style.display = 'none';
    }

    const projectionConfig = Projection.PROJECTIONS[this.getMapState(mapId).currentProjection];
    if (bbox) {
      // GV There were issues with fromLonLat in rare cases in LCC projections, transformExtentFromProj seems to solve them.
      // GV fromLonLat and transformExtentFromProj give differing results in many cases, fromLonLat had issues with the first
      // GV three results from a geolocator search for "vancouver river"
      const convertedExtent = Projection.transformExtentFromProj(bbox, Projection.getProjectionLonLat(), projectionConfig);

      // Highlight
      this.getMapViewerLayerAPI(mapId).featureHighlight.highlightGeolocatorBBox(convertedExtent);

      // Zoom to extent and await
      await this.zoomToExtent(mapId, convertedExtent, {
        padding: [50, 50, 50, 50],
        maxZoom: 16,
        duration: OL_ZOOM_DURATION,
      });

      // Now show the click marker icon
      this.clickMarkerIconShow(mapId, { lonlat: coords });
      for (let i = 0; i < indicatorBox.length; i++) {
        (indicatorBox[i] as HTMLElement).style.display = '';
      }
    } else {
      const projectedCoords = Projection.transformPoints(
        [coords],
        Projection.PROJECTION_NAMES.LONLAT,
        `EPSG:${this.getMapStateProtected(mapId).currentProjection}`
      );

      const extent: Extent = [...projectedCoords[0], ...projectedCoords[0]];

      // Zoom to extent and await
      await this.zoomToExtent(mapId, extent);

      // Now show the click marker icon
      this.clickMarkerIconShow(mapId, { lonlat: coords });
      for (let i = 0; i < indicatorBox.length; i++) {
        (indicatorBox[i] as HTMLElement).style.display = '';
      }
    }
  }

  /**
   * Return to initial view state of map using config.
   *
   * @param {string} mapId - ID of the map to return to original view
   * @returns Promise<void>
   * @static
   */
  static async zoomToInitialExtent(mapId: string): Promise<void> {
    const currProjection = this.getMapStateProtected(mapId).currentProjection;
    let extent: Extent | undefined = MAP_EXTENTS[currProjection];
    const options: FitOptions = { padding: OL_ZOOM_PADDING, duration: OL_ZOOM_DURATION };
    const homeView = this.getMapStateProtected(mapId).homeView || this.getMapStateProtected(mapId).initialView;

    // Transform center coordinates and update options if zoomAndCenter are in config
    if (homeView.zoomAndCenter) {
      [options.maxZoom] = homeView.zoomAndCenter!;

      const center = homeView.zoomAndCenter[1];
      const projectedCoords = Projection.transformPoints([center], Projection.PROJECTION_NAMES.LONLAT, `EPSG:${currProjection}`);

      extent = [...projectedCoords[0], ...projectedCoords[0]];
    }

    // If extent is in config, use it
    if (homeView.extent) {
      const lonlatExtent = homeView.extent as Extent;
      // If extent is not lon/lat, we assume it is in the map projection and use it as is.
      extent = GeoUtilities.isExtentLonLat(lonlatExtent)
        ? Projection.transformExtentFromProj(
            lonlatExtent,
            Projection.getProjectionLonLat(),
            Projection.getProjectionFromString(`EPSG:${currProjection}`)
          )
        : lonlatExtent;

      options.padding = [0, 0, 0, 0];
    }

    // If layer IDs are in the config, use them
    if (homeView.layerIds) extent = await this.getMapViewerLayerAPI(mapId).getExtentOfMultipleLayers(homeView.layerIds);

    // If extent is not valid, take the default one for the current projection
    if (!extent || extent.length !== 4 || extent.includes(Infinity))
      extent = Projection.transformExtentFromProj(
        MAP_EXTENTS[currProjection],
        Projection.getProjectionLonLat(),
        Projection.getProjectionFromString(`EPSG:${currProjection}`)
      );

    return this.zoomToExtent(mapId, extent, options);
  }

  /**
   * Zoom to geolocation position provided.
   *
   * @param {string} mapId - ID of map to zoom on
   * @param {GeolocationPosition} position - Info on position to zoom to.
   * @returns Promise<void>
   * @static
   */
  static zoomToMyLocation(mapId: string, position: GeolocationPosition): Promise<void> {
    const coord: Coordinate = [position.coords.longitude, position.coords.latitude];
    const projectedCoords = Projection.transformPoints(
      [coord],
      Projection.PROJECTION_NAMES.LONLAT,
      `EPSG:${this.getMapStateProtected(mapId).currentProjection}`
    );

    const extent: Extent = [...projectedCoords[0], ...projectedCoords[0]];

    return this.zoomToExtent(mapId, extent);
  }

  /**
   * Zoom to layer visible scale.
   *
   * @param {string} mapId - ID of map to zoom on
   * @param {string} layerPath - Path of layer to zoom to.
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path.
   * @static
   */
  static zoomToLayerVisibleScale(mapId: string, layerPath: string): void {
    const view = this.getMapViewer(mapId).getView();
    const mapZoom = view.getZoom();
    const geoviewLayer = MapEventProcessor.getMapViewerLayerAPI(mapId).getGeoviewLayer(layerPath);
    const layerMaxZoom = geoviewLayer.getMaxZoom();
    const layerMinZoom = geoviewLayer.getMinZoom();

    // Set the right zoom (Infinity will act as a no change in zoom level)
    let layerZoom = Infinity;
    if (layerMinZoom !== -Infinity && layerMinZoom >= mapZoom!) layerZoom = layerMinZoom + 0.25;
    else if (layerMaxZoom !== Infinity && layerMaxZoom <= mapZoom!) layerZoom = layerMaxZoom - 0.25;

    // Change view to go to proper zoom centered in the middle of layer extent
    // If there is no layerExtent or if the zoom needs to zoom out, the center will be undefined and not use
    // Check if the map center is already in the layer extent and if so, do not center
    geoviewLayer
      .getBounds(this.getMapViewer(mapId).getProjection(), MapViewer.DEFAULT_STOPS)
      .then((layerExtent) => {
        const centerExtent =
          layerExtent && layerMinZoom > mapZoom! && !GeoUtilities.isPointInExtent(view.getCenter()!, layerExtent)
            ? [(layerExtent[2] + layerExtent[0]) / 2, (layerExtent[1] + layerExtent[3]) / 2]
            : undefined;

        view.animate({
          center: centerExtent,
          zoom: layerZoom,
          duration: OL_ZOOM_DURATION,
        });
      })
      .catch((error: unknown) => {
        // Log error
        logger.logPromiseFailed('in getBounds in MapEventProcessor.zoomToLayerVisibleScale', error);
      });
  }

  /**
   * Zoom to extents of a layer.
   *
   * @param mapId - ID of map to zoom on
   * @param layerPath - The path of the layer to zoom to.
   * @throws {NoBoundsError} When the layer doesn't have bounds.
   */
  static zoomToLayerExtent(mapId: string, layerPath: string, fitOptions?: FitOptions): Promise<void> {
    // Define some zoom options
    const options: FitOptions = fitOptions ?? { padding: OL_ZOOM_PADDING, duration: OL_ZOOM_DURATION };

    // Get the layer bounds
    const bounds = LegendEventProcessor.getLayerBounds(mapId, layerPath);

    // If found
    if (bounds) {
      return MapEventProcessor.zoomToExtent(mapId, bounds, options);
    }

    // Failed
    throw new NoBoundsError(layerPath);
  }

  /**
   * Set Z index for layers
   *
   * @param mapId - Id of map to set layer Z indices
   */
  static setLayerZIndices = (mapId: string): void => {
    const reversedLayers = [...this.getMapStateProtected(mapId).orderedLayerInfo].reverse();
    reversedLayers.forEach((orderedLayerInfo, index) => {
      const gvLayer = this.getMapViewerLayerAPI(mapId).getGeoviewLayerIfExists(orderedLayerInfo.layerPath);
      gvLayer?.setZIndex(index + 10);
    });
  };

  static getPixelFromCoordinate = (mapId: string, coord: Coordinate): Pixel => {
    return this.getMapViewer(mapId).map.getPixelFromCoordinate(coord);
  };

  static setClickMarkerOnPosition = (mapId: string, position: number[]): void => {
    this.getMapViewer(mapId).map.getOverlayById(`${mapId}-clickmarker`)!.setPosition(position);
  };

  /**
   * Get all active filters for layer.
   *
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The path for the layer to get filters from.
   * @returns {LayerFilters} The active layer filters
   * @throws {LayerNotFoundError} When the layer couldn't be found at the given layer path.
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path.
   * @static
   */
  static getActiveFilters(mapId: string, layerPath: string): LayerFilters {
    // Get the layer and layer config
    const layer = this.getMapViewerLayerAPI(mapId).getGeoviewLayerRegular(layerPath);
    const layerConfig = layer.getLayerConfig();

    // The initial filter
    const initialFilter = layerConfig.getLayerFilter();

    // The class breaks filter if any
    const classFilter = layer.getFilterFromStyle();

    // The data table filter if any
    const dataFilter = DataTableEventProcessor.getTableFilter(mapId, layerPath);

    // If the TimeSlider is initialized
    let timeFilter: string | undefined;
    if (TimeSliderEventProcessor.isTimeSliderInitialized(mapId)) {
      // Assign it for the return
      timeFilter = TimeSliderEventProcessor.getTimeSliderFilter(mapId, layerPath);
    }

    // Return the current filters in the application
    return new LayerFilters(initialFilter, classFilter, dataFilter, timeFilter);
  }

  /**
   * Apply all available filters to layer.
   * @param {string} mapId - The map id.
   * @param {string} layerPath - The path of the layer to apply filters to.
   * @returns {void}
   * @throws {LayerWrongTypeError} When the layer is of wrong type at the given layer path.
   * @static
   */
  static applyLayerFilters(mapId: string, layerPath: string): void {
    // Get the Geoview layer
    const geoviewLayer = MapEventProcessor.getMapViewerLayerAPI(mapId).getGeoviewLayerRegularIfExists(layerPath);

    // If found it
    if (geoviewLayer) {
      // Read filter information from the UI
      const layerFilters = this.getActiveFilters(mapId, layerPath);

      // Apply the view filter on the layer
      geoviewLayer.setLayerFilters(layerFilters, true);
    }
  }

  /**
   * Loads a plugin script dynamically and adds the plugin to a map.
   * This method first loads the plugin script by name, then registers the
   * plugin with the {@link MapEventProcessor} for the specified map.
   * @param {string} mapId - The unique identifier of the map to which the plugin will be added.
   * @param {string} pluginName - The name of the plugin to load and register.
   * @returns {Promise<void>} A promise that resolves when the plugin has been successfully loaded
   * and added to the map, or rejects with a formatted error if loading or registration fails.
   * @static
   */
  static loadAndAddPlugin(mapId: string, pluginName: string): Promise<void> {
    // Create a promise that will resolve when the plugin is added
    return new Promise<void>((resolve, reject) => {
      Plugin.loadScript(pluginName)
        .then((typePlugin) => {
          // add the plugin by passing in the loaded constructor from the script tag
          MapEventProcessor.addPlugin(pluginName, typePlugin, mapId)
            .then(() => {
              // Plugin added
              resolve();
            })
            .catch((error: unknown) => {
              // Reject
              reject(formatError(error));
            });
        })
        .catch((error: unknown) => {
          // Reject
          reject(formatError(error));
        });
    });
  }

  /**
   * Add new plugin
   * @param {string} pluginId - The plugin id
   * @param {typeof AbstractPlugin} constructor - The plugin class (React Component)
   * @param {string} mapId - Id of map to add this plugin to
   * @param {unknown} props - The plugin options
   * @returns {Promise<AbstractPlugin>} A Promise which resolves with the Plugin instance.
   * @static
   */
  static async addPlugin(pluginId: string, constructor: typeof AbstractPlugin, mapId: string, props?: unknown): Promise<AbstractPlugin> {
    // Get the MapViewer
    const mapViewer = this.getMapViewer(mapId);

    // If the plugin is already loaded, return it
    if (mapViewer.plugins[pluginId]) return mapViewer.plugins[pluginId];

    // If no constructor provided
    if (!constructor) throw new PluginError(pluginId, mapId);

    // Construct the Plugin class
    // create new instance of the plugin. Here we must type the constructor variable to any
    // in order to cancel the "'new' expression, whose target lacks a construct signature" error message
    // ? unknown type cannot be use, need to escape
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const plugin: AbstractPlugin = new (constructor as any)(pluginId, mapViewer, props);

    // Attach to the map plugins object
    mapViewer.plugins[pluginId] = plugin;

    // a config object used to store package config
    let pluginConfigObj: unknown = {};

    // if a schema is defined then look for a config for this plugin
    if (plugin.schema && plugin.defaultConfig) {
      const schema = plugin.schema();
      const defaultConfig = plugin.defaultConfig();

      // create a validator object
      const validator = new Ajv({
        strict: false,
        allErrors: true,
      });

      // initialize validator with schema file
      const validate = validator.compile(schema as AnySchema);

      // if no config is provided then use default
      pluginConfigObj = defaultConfig;

      /**
       * If a user is using map config from a file then attempt to look
       * for custom config for loaded core packages on the same path of the map config.
       * If none exists then load the default config
       */
      const configUrl = document.getElementById(mapViewer.mapId)?.getAttribute('data-config-url');

      // Check if there is a corePackageConfig for the plugin
      const configObj = mapViewer.getCorePackageConfig(pluginId);

      // If there is an inline config use it, if not try to read the file config associated with map config
      if (configObj) {
        logger.logTraceCore('Plugin - addPlugin inline config', configObj);
        pluginConfigObj = configObj;
      } else if (configUrl) {
        const configPath = `${configUrl.split('.json')[0]}-${pluginId}.json`;

        try {
          // Try to find the custom config from the config path
          const result = await Fetch.fetchJson(configPath);

          if (result) {
            logger.logTraceCore('Plugin - addPlugin file config', result);
            pluginConfigObj = result;
          }
        } catch (error: unknown) {
          // Log warning
          logger.logWarning(`Config not found.`, error);
          // Notify with a warning
          mapViewer.notifications.addNotificationWarning('error.map.pluginConfigNotFound', [pluginId, mapId, configPath]);
        }
      }

      // validate configuration
      const valid = validate(pluginConfigObj);

      if (!valid && validate.errors && validate.errors.length) {
        for (let j = 0; j < validate.errors.length; j += 1) {
          const error = validate.errors[j];
          const errorMessage = `Plugin ${pluginId}: ${error.instancePath} ${error.message} - ${JSON.stringify(error.params)}`;

          // Log
          logger.logError(errorMessage);
          // Don't show error message as it can contain non-translated elements via Ajv error messages, only log for now
          // api.getMapViewer(mapId).notifications.showError(errorMessage);
        }
      }
    }

    // Set the config
    plugin.setConfig(pluginConfigObj);

    // add translations if provided
    Object.entries(plugin.defaultTranslations()).forEach(([languageKey, value]) => {
      // Add the resource bundle to support the plugin language
      i18next.addResourceBundle(languageKey, 'translation', value, true, false);
    });

    // Call plugin add method
    plugin.add();

    // Return the plugin
    return plugin;
  }

  // #endregion

  // TODO: Move this section to config API after refactor
  // #region CONFIG FROM MAP STATE

  /**
   * Creates layer initial settings according to provided configs.
   * @param {ConfigBaseClass} layerEntryConfig - Layer entry config for the layer.
   * @param {TypeOrderedLayerInfo} orderedLayerInfo - Ordered layer info for the layer.
   * @param {TypeLegendLayer} legendLayerInfo - Legend layer info for the layer.
   * @returns {TypeLayerInitialSettings} Initial settings object.
   * @static
   * @private
   */
  static #getInitialSettings(
    layerEntryConfig: ConfigBaseClass,
    orderedLayerInfo: TypeOrderedLayerInfo,
    legendLayerInfo: TypeLegendLayer
  ): TypeLayerInitialSettings {
    return {
      states: {
        visible: orderedLayerInfo.visible,
        opacity: legendLayerInfo.opacity,
        legendCollapsed: orderedLayerInfo.legendCollapsed,
        queryable: orderedLayerInfo.queryableState,
        hoverable: orderedLayerInfo.hoverable,
      },
      controls: layerEntryConfig.getInitialSettings()?.controls,
      bounds: layerEntryConfig.getInitialSettingsBounds(),
      extent: layerEntryConfig.getInitialSettingsExtent(),
      className: layerEntryConfig.getInitialSettingsClassName(),
      minZoom: layerEntryConfig.getInitialSettings()?.minZoom,
      maxZoom: layerEntryConfig.getInitialSettings()?.maxZoom,
    };
  }

  /**
   * Creates a layer entry config based on current layer state.
   * @param {string} mapId - Id of map.
   * @param {string} layerPath - Path of the layer to create config for.
   * @param {boolean} isGeocore - Indicates if it is a geocore layer.
   * @param {boolean | 'hybrid'} overrideGeocoreServiceNames - Indicates if geocore layer names should be kept as is or returned to defaults.
   * @returns {TypeLayerEntryConfig} Entry config object.
   * @throws {LayerConfigNotFoundError} When the layer configuration couldn't be found at the given layer path.
   * @static
   * @private
   */
  static #createLayerEntryConfig(
    mapId: string,
    layerPath: string,
    isGeocore: boolean,
    overrideGeocoreServiceNames: boolean | 'hybrid'
  ): TypeLayerEntryConfig {
    // Get needed info
    const layerEntryConfig = this.getMapViewerLayerAPI(mapId).getLayerEntryConfig(layerPath);
    const orderedLayerInfo = this.findMapLayerFromOrderedInfo(mapId, layerPath);
    const legendLayerInfo = LegendEventProcessor.getLegendLayerInfo(mapId, layerPath);

    // Get original layerEntryConfig from map config
    const pathArray = layerPath.split('/');
    if (pathArray[0] === pathArray[1]) pathArray.splice(0, 1);
    const geoviewLayerConfig = this.getGeoViewMapConfig(mapId)?.map.listOfGeoviewLayerConfig?.find(
      (layerConfig) => layerConfig.geoviewLayerId === pathArray[0]
    );

    let configLayerEntryConfig;
    if (geoviewLayerConfig) {
      configLayerEntryConfig = (geoviewLayerConfig as TypeGeoviewLayerConfig).listOfLayerEntryConfig?.find(
        (nextEntryConfig) => nextEntryConfig.layerId === pathArray[1]
      );
      for (let i = 2; i < pathArray.length; i++) {
        if (configLayerEntryConfig?.listOfLayerEntryConfig)
          configLayerEntryConfig = configLayerEntryConfig.listOfLayerEntryConfig.find(
            (nextEntryConfig: TypeLayerEntryConfig) => nextEntryConfig.layerId === pathArray[i]
          );
        else configLayerEntryConfig = undefined;
      }
    }

    // Create list of sublayer entry configs if it is a group layer
    const listOfLayerEntryConfig: TypeLayerEntryConfig[] = [];
    if (layerEntryConfig.getEntryTypeIsGroup()) {
      const sublayerPaths = this.getMapLayerOrder(mapId).filter(
        (entryLayerPath) =>
          entryLayerPath.startsWith(`${layerPath}/`) && entryLayerPath.split('/').length === layerPath.split('/').length + 1
      );
      sublayerPaths.forEach((sublayerPath) =>
        listOfLayerEntryConfig.push(this.#createLayerEntryConfig(mapId, sublayerPath, isGeocore, overrideGeocoreServiceNames))
      );
    }

    // Get initial settings
    const initialSettings = this.#getInitialSettings(layerEntryConfig, orderedLayerInfo!, legendLayerInfo!);

    // Clone the source object
    let source;
    if (layerEntryConfig instanceof AbstractBaseLayerEntryConfig) {
      source = layerEntryConfig.cloneSource();
    }

    // Only use feature info specified in original config, not drawn from services
    if (source?.featureInfo) delete source?.featureInfo;
    const configLayerEntryConfigFeatureInfo = AbstractBaseLayerEntryConfig.getClassOrTypeFeatureInfo(configLayerEntryConfig);
    if (source && configLayerEntryConfigFeatureInfo) source.featureInfo = configLayerEntryConfigFeatureInfo;

    if (source?.dataAccessPath && isGeocore && overrideGeocoreServiceNames !== true) source.dataAccessPath = undefined;

    const layerStyle =
      legendLayerInfo!.styleConfig && (!isGeocore || overrideGeocoreServiceNames === true) ? legendLayerInfo!.styleConfig : undefined;

    const layerText = layerEntryConfig instanceof VectorLayerEntryConfig ? layerEntryConfig.getLayerText() : undefined;

    // Construct layer entry config
    const newLayerEntryConfig = {
      layerId: layerEntryConfig.layerId,
      layerName: isGeocore && overrideGeocoreServiceNames === false ? undefined : layerEntryConfig.getLayerName(),
      layerFilter: AbstractBaseLayerEntryConfig.getClassOrTypeLayerFilter(configLayerEntryConfig),
      initialSettings,
      layerStyle,
      layerText,
      entryType: listOfLayerEntryConfig.length ? 'group' : undefined,
      source: listOfLayerEntryConfig.length ? undefined : source,
      listOfLayerEntryConfig: listOfLayerEntryConfig.length ? listOfLayerEntryConfig : undefined,
    };

    return newLayerEntryConfig as unknown as TypeLayerEntryConfig;
  }

  /**
   * Creates a geoview layer config based on current layer state.
   * @param {string} mapId - Id of map.
   * @param {string} layerPath - Path of the layer to create config for.
   * @param {boolean | "hybrid"} overrideGeocoreServiceNames - Indicates if geocore layer names should be kept as is or returned to defaults.
   * @returns {MapConfigLayerEntry | undefined} Geoview layer config object.
   * @static
   * @private
   */
  static #createGeoviewLayerConfig(
    mapId: string,
    layerPath: string,
    overrideGeocoreServiceNames: boolean | 'hybrid'
  ): MapConfigLayerEntry | undefined {
    // Get needed info
    const layerEntryConfig = this.getMapViewerLayerAPI(mapId).getLayerEntryConfigIfExists(layerPath);
    const geoviewLayerConfig = layerEntryConfig?.getGeoviewLayerConfig();

    // If not found, log warning and skip
    if (!layerEntryConfig || !geoviewLayerConfig) {
      // TODO: Check if better to use getLayerEntryConfig instead of getLayerEntryConfigIfExists above and have an error be thrown?
      // Log
      logger.logWarning(`Couldn't find the layer entry config for layer path '${layerPath}'`);
      return undefined;
    }

    // Get info
    const orderedLayerInfo = this.findMapLayerFromOrderedInfo(mapId, layerPath);
    const legendLayerInfo = LegendEventProcessor.getLegendLayerInfo(mapId, layerPath);

    // Check if the layer is a geocore layers
    const isGeocore = isValidUUID(layerPath.split('/')[0]);

    // If is a group
    let layerEntryLayerPaths: string[] = [];
    if (layerEntryConfig instanceof GroupLayerEntryConfig) {
      layerEntryLayerPaths = layerEntryConfig.getLayerPaths();
    }

    // Check for sublayers
    const sublayerPaths = this.getMapLayerOrder(mapId).filter(
      // We only want the immediate child layers, group sublayers will handle their own sublayers
      (entryLayerPath) => layerEntryLayerPaths.includes(entryLayerPath)
    );

    // Build list of sublayer entry configs
    const listOfLayerEntryConfig: TypeLayerEntryConfig[] = [];
    if (sublayerPaths.length && layerEntryConfig.layerId === 'base-group')
      sublayerPaths.forEach((sublayerPath) =>
        listOfLayerEntryConfig.push(this.#createLayerEntryConfig(mapId, sublayerPath, isGeocore, overrideGeocoreServiceNames))
      );
    else listOfLayerEntryConfig.push(this.#createLayerEntryConfig(mapId, layerPath, isGeocore, overrideGeocoreServiceNames));

    // Get initial settings
    const initialSettings = this.#getInitialSettings(layerEntryConfig, orderedLayerInfo!, legendLayerInfo!);

    // Construct geoview layer config
    const newGeoviewLayerConfig: MapConfigLayerEntry =
      isGeocore && overrideGeocoreServiceNames !== true
        ? {
            geoviewLayerId: geoviewLayerConfig.geoviewLayerId,
            geoviewLayerName: overrideGeocoreServiceNames === false ? undefined : layerEntryConfig.getGeoviewLayerName(),
            geoviewLayerType: 'geoCore',
            initialSettings,
            useAsBasemap: geoviewLayerConfig.useAsBasemap,
            listOfLayerEntryConfig,
          }
        : {
            geoviewLayerId: geoviewLayerConfig.geoviewLayerId,
            geoviewLayerName: geoviewLayerConfig.geoviewLayerName,
            geoviewLayerType: geoviewLayerConfig.geoviewLayerType,
            initialSettings,
            isTimeAware: geoviewLayerConfig.isTimeAware,
            listOfLayerEntryConfig,
            metadataAccessPath: geoviewLayerConfig.metadataAccessPath,
            serviceDateFormat: geoviewLayerConfig.serviceDateFormat,
            serviceDateFormatIdentify: geoviewLayerConfig.serviceDateFormatIdentify,
            serviceDateTimezone: geoviewLayerConfig.serviceDateTimezone,
            serviceDateTemporalMode: geoviewLayerConfig.serviceDateTemporalMode,
            displayDateFormat: geoviewLayerConfig.displayDateFormat,
            displayDateTimezone: geoviewLayerConfig.displayDateTimezone,
            useAsBasemap: geoviewLayerConfig.useAsBasemap,
          };

    return newGeoviewLayerConfig;
  }

  /**
   * Creates a geoview layer config based on current layer state.
   * @param {string} mapId - Id of map.
   * @returns {TypeTimeSliderProps[] | undefined} Array of time slider props.
   * @static
   * @private
   */
  static #createTimeSliderConfigs(mapId: string): TypeTimeSliderProps[] | undefined {
    // Get time slider info
    const timeSliderLayers = TimeSliderEventProcessor.getTimeSliderLayers(mapId);

    if (timeSliderLayers) {
      const timeSliderProps: TypeTimeSliderProps[] = [];
      Object.keys(timeSliderLayers).forEach((layerPath) => {
        // Get values from time slider layers
        const {
          additionalLayerpaths,
          isMainLayerPath,
          title,
          description,
          locked,
          reversed,
          values,
          delay: delayTimeSlider,
          filtering,
          range,
          discreteValues,
          displayDateFormat,
          displayDateFormatShort,
          serviceDateTemporalMode,
          displayDateTimezone,
          field,
        } = timeSliderLayers[layerPath];

        if (isMainLayerPath) {
          // Build time dimension
          const timeDimension: TimeDimension = {
            field,
            default: values.map((value) => DateMgt.formatDateISOShort(value)),
            nearestValues: discreteValues ? 'discrete' : 'continuous',
            displayDateFormat,
            displayDateFormatShort,
            serviceDateTemporalMode,
            displayDateTimezone,
            rangeItems: {
              type: '',
              range,
            },
            singleHandle: values.length === 1,
            isValid: true,
          };

          const fields = [field];
          if (additionalLayerpaths) {
            additionalLayerpaths.forEach((additionalLayerPath) => fields.push(timeSliderLayers[additionalLayerPath].field));
          }

          // Add info to prop list
          timeSliderProps.push({
            layerPaths: additionalLayerpaths ? [layerPath, ...additionalLayerpaths] : [layerPath],
            title,
            description,
            delay: delayTimeSlider,
            fields,
            filtering,
            locked,
            reversed,
            timeDimension,
          });
        }
      });

      return timeSliderProps;
    }

    return undefined;
  }

  /**
   * Creates a map config based on current map state.
   * @param {string} mapId - Id of map.
   * @param {boolean | "hybrid"} overrideGeocoreServiceNames - Indicates if geocore layer names should be kept as is or returned to defaults.
   * @returns {TypeMapFeaturesInstance | undefined} The type map features instance
   * @static
   */
  static createMapConfigFromMapState(
    mapId: string,
    overrideGeocoreServiceNames: boolean | 'hybrid' = true
  ): TypeMapFeaturesInstance | undefined {
    const config = this.getGeoViewMapConfig(mapId);

    if (config) {
      // Get paths of top level layers
      const layerOrder = this.getMapLayerOrder(mapId).filter(
        (layerPath) => !this.getMapViewerLayerAPI(mapId).getLayerEntryConfigIfExists(layerPath)?.getParentLayerConfig()
      );

      // Build list of geoview layer configs
      const listOfGeoviewLayerConfig = layerOrder
        .map((layerPath) => this.#createGeoviewLayerConfig(mapId, layerPath, overrideGeocoreServiceNames))
        .filter((mapLayerEntry) => !!mapLayerEntry);

      // Get info for view
      const projection = this.getMapStateProtected(mapId).currentProjection;
      const currentView = this.getMapViewer(mapId).map.getView();
      const currentCenter = currentView.getCenter();
      const currentProjection = currentView.getProjection().getCode();
      const centerLatLng = Projection.transformPoints([currentCenter!], currentProjection, Projection.PROJECTION_NAMES.LONLAT)[0] as [
        number,
        number,
      ];

      // Set view settings
      const viewSettings: TypeViewSettings = {
        initialView: { zoomAndCenter: [currentView.getZoom() as number, centerLatLng] },
        homeView: this.getMapStateProtected(mapId).homeView,
        enableRotation: config.map.viewSettings.enableRotation !== undefined ? config.map.viewSettings.enableRotation : undefined,
        rotation: this.getMapStateProtected(mapId).rotation,
        minZoom: currentView.getMinZoom(),
        maxZoom: currentView.getMaxZoom(),
        maxExtent: this.getGeoViewMapConfig(mapId)?.map.viewSettings.maxExtent,
        projection,
      };

      // Set map config settings
      const map: TypeMapConfig = {
        basemapOptions: this.getCurrentBasemapOptions(mapId),
        interaction: this.getMapInteraction(mapId),
        listOfGeoviewLayerConfig,
        highlightColor: config.map.highlightColor,
        overlayObjects: { pointMarkers: this.getPointMarkers(mapId) },
        viewSettings,
      };

      let { corePackagesConfig } = config;
      // Create time slider config and add to core package configs
      if (TimeSliderEventProcessor.isTimeSliderInitialized(mapId)) {
        const sliders = this.#createTimeSliderConfigs(mapId);
        if (corePackagesConfig && sliders) {
          const configObj = corePackagesConfig?.find((packageConfig) => Object.keys(packageConfig).includes('time-slider'));
          if (configObj) configObj['time-slider'] = { sliders };
          else corePackagesConfig.push({ 'time-slider': { sliders } });
        } else if (sliders) corePackagesConfig = [{ 'time-slider': { sliders } }];
      }

      // Construct map config
      const newMapConfig: TypeMapFeaturesInstance = {
        map,
        theme: AppEventProcessor.getDisplayTheme(mapId),
        navBar: config.navBar,
        footerBar: config.footerBar,
        appBar: config.appBar,
        overviewMap: config.overviewMap,
        components: config.components,
        corePackages: config.corePackages,
        corePackagesConfig,
        externalPackages: config.externalPackages,
        serviceUrls: config.serviceUrls,
        schemaVersionUsed: config.schemaVersionUsed,
        globalSettings: config.globalSettings,
      };

      // Set app bar tab settings
      if (newMapConfig.appBar) {
        newMapConfig.appBar.selectedTab = UIEventProcessor.getActiveAppBarTab(mapId).tabId as TypeValidAppBarCoreProps;

        const selectedDataTableLayerPath = DataTableEventProcessor.getSingleDataTableState(mapId, 'selectedLayerPath');
        if (selectedDataTableLayerPath) newMapConfig.appBar.selectedDataTableLayerPath = selectedDataTableLayerPath as string;
        const selectedLayerPath = LegendEventProcessor.getLayerPanelState(mapId, 'selectedLayerPath');
        if (selectedLayerPath) newMapConfig.appBar.selectedLayersLayerPath = selectedLayerPath as string;
      }

      // Set footer bar tab settings
      if (newMapConfig.footerBar) {
        newMapConfig.footerBar.selectedTab = UIEventProcessor.getActiveFooterBarTab(mapId).tabId as TypeValidFooterBarTabsCoreProps;

        const selectedDataTableLayerPath = DataTableEventProcessor.getSingleDataTableState(mapId, 'selectedLayerPath');
        if (selectedDataTableLayerPath) newMapConfig.footerBar.selectedDataTableLayerPath = selectedDataTableLayerPath as string;
        const selectedLayerLayerPath = LegendEventProcessor.getLayerPanelState(mapId, 'selectedLayerPath');
        if (selectedLayerLayerPath) newMapConfig.footerBar.selectedLayersLayerPath = selectedLayerLayerPath as string;

        // If the TimeSlider plugin is initialized
        if (TimeSliderEventProcessor.isTimeSliderInitialized(mapId)) {
          // Store it
          newMapConfig.footerBar.selectedTimeSliderLayerPath = TimeSliderEventProcessor.getTimeSliderSelectedLayer(mapId);
        }
      }

      return newMapConfig;
    }

    return undefined;
  }

  /**
   * Searches through a map config and replaces any matching layer names with their provided partner.
   *
   * @param {string[][]} namePairs -  The array of name pairs. Presumably one english and one french name in each pair.
   * @param {TypeMapFeaturesInstance} mapConfig - The config to modify.
   * @param {boolean} removeUnlisted - Remove any layer name that doesn't appear in namePairs.
   * @returns {TypeMapFeaturesInstance} Map config with updated names.
   * @static
   */
  static replaceMapConfigLayerNames(
    namePairs: string[][],
    mapConfig: TypeMapFeaturesInstance,
    removeUnlisted: boolean = false
  ): TypeMapFeaturesInstance {
    const pairsDict: Record<string, string> = {};
    namePairs.forEach((pair) => {
      [pairsDict[pair[1]], pairsDict[pair[0]]] = pair;
    });

    mapConfig.map.listOfGeoviewLayerConfig?.forEach((geoviewLayerConfig) => {
      if (geoviewLayerConfig.geoviewLayerName && pairsDict[geoviewLayerConfig.geoviewLayerName])
        // eslint-disable-next-line no-param-reassign
        geoviewLayerConfig.geoviewLayerName = pairsDict[geoviewLayerConfig.geoviewLayerName];
      // eslint-disable-next-line no-param-reassign
      else if (removeUnlisted) geoviewLayerConfig.geoviewLayerName = '';
      if (geoviewLayerConfig.listOfLayerEntryConfig?.length)
        this.#replaceLayerEntryConfigNames(pairsDict, geoviewLayerConfig.listOfLayerEntryConfig, removeUnlisted);
    });

    return mapConfig;
  }

  /**
   * Searches through a list of layer entry configs and replaces any matching layer names with their provided partner.
   *
   * @param {Record<string, string>} pairsDict -  The dict of name pairs. Presumably one english and one french name in each pair.
   * @param {TypeLayerEntryConfig[]} listOfLayerEntryConfigs - The layer entry configs to modify.
   * @param {boolean} removeUnlisted - Remove any layer name that doesn't appear in namePairs.
   * @returns {void}
   * @static
   */
  static #replaceLayerEntryConfigNames(
    pairsDict: Record<string, string>,
    listOfLayerEntryConfigs: TypeLayerEntryConfig[],
    removeUnlisted: boolean
  ): void {
    listOfLayerEntryConfigs?.forEach((layerEntryConfig) => {
      const layerName = ConfigBaseClass.getClassOrTypeLayerName(layerEntryConfig);
      // If there's a name in pairsDict that matches
      if (layerName && pairsDict[layerName]) ConfigBaseClass.setClassOrTypeLayerName(layerEntryConfig, pairsDict[layerName]);
      else if (removeUnlisted) ConfigBaseClass.setClassOrTypeLayerName(layerEntryConfig, '');
      if (layerEntryConfig.listOfLayerEntryConfig?.length)
        this.#replaceLayerEntryConfigNames(pairsDict, layerEntryConfig.listOfLayerEntryConfig, removeUnlisted);
    });
  }

  /**
   * Creates a new geometry group on the map if it doesn't already exist.
   * Geometry groups are used to organize and manage collections of vector features (lines, polygons, points).
   * @param {string} mapId - The map identifier
   * @param {string} groupName - The unique name for the geometry group to create
   * @returns {void}
   * @static
   */
  static createGeometryGroup(mapId: string, groupName: string): void {
    const layerApi = this.getMapViewerLayerAPI(mapId);
    if (!layerApi.geometry.hasGeometryGroup(groupName)) {
      layerApi.geometry.createGeometryGroup(groupName);
    }
  }

  /**
   * Deletes all geometries from a geometry group.
   * Removes all vector features (lines, polygons, points) that belong to the specified group.
   * The group itself remains and can be reused.
   * @param {string} mapId - The map identifier
   * @param {string} groupName - The name of the geometry group to clear
   * @returns {void}
   * @static
   */
  static deleteGeometriesFromGroup(mapId: string, groupName: string): void {
    const layerApi = this.getMapViewerLayerAPI(mapId);
    if (layerApi.geometry.hasGeometryGroup(groupName)) {
      layerApi.geometry.deleteGeometriesFromGroup(groupName);
    }
  }

  /**
   * Adds an overlay to the map.
   * Overlays are HTML DOM elements positioned at map coordinates that float above the map canvas.
   * Common uses include tooltips, popups, and measurement labels.
   * @param {string} mapId - The map identifier
   * @param {Overlay} overlay - The OpenLayers overlay to add to the map.
   * @returns {void}
   * @static
   */
  static addOverlay(mapId: string, overlay: Overlay): void {
    const viewer = this.getMapViewer(mapId);
    if (overlay) viewer.map.addOverlay(overlay);
  }

  /**
   * Removes an overlay from the map.
   * Removes the HTML element from the map display and cleans up references.
   * @param {string} mapId - The map identifier
   * @param {Overlay} overlay - The OpenLayers overlay to remove from the map.
   * @returns {void}
   * @static
   */
  static removeOverlay(mapId: string, overlay: Overlay): void {
    const viewer = this.getMapViewer(mapId);
    if (overlay) viewer.map.removeOverlay(overlay);
  }

  /**
   * Initializes drawing interactions on the given vector source
   * @param {string} mapId - The map identifier
   * @param {string} geomGroupKey - The geometry group key in which to hold the geometries
   * @param {string} type - The type of geometry to draw (Polygon, LineString, Circle, etc)
   * @param {TypeFeatureStyle} [style] - The styles for the drawing
   * @returns {Draw} The init draw interactions object
   * @static
   */
  static initDrawInteractions(mapId: string, geomGroupKey: string, type: string, style: TypeFeatureStyle): Draw {
    return this.getMapViewer(mapId).initDrawInteractions(geomGroupKey, type, style);
  }
  // #endregion
}
